import apis from './apis';

/**
 * Time saved (F-TIME): manual hours → hours with PONS → hours saved → value.
 * Backed by /time-saved. Money is in the firm's currency (lowercase ISO 4217).
 *
 * Hours with PONS are MEASURED - the item's processing time (R4-14). Only the
 * manual hours ("how long this usually takes by hand") are ever entered.
 */

export type TimeSavedKind = 'matter' | 'review' | 'draft' | 'research' | 'table';
export type TimeSavedItemKind = 'draft' | 'review' | 'table';

/** Which rule an estimate came from, most specific first. */
export type TimeSavedSource = 'item' | 'firm_type' | 'firm_kind' | 'default_type' | 'default';

export interface TimeSavedAssumptionRow {
  kind: TimeSavedKind;
  type_key?: string | null;
  name?: string | null;
  /** null = the built-in volume estimate (no flat number). */
  manual_hours: number | null;
  pons_hours: number | null;
  is_default: boolean;
}

/** Whether the hours with PONS were measured from the item, or estimated. */
export type PonsSource = 'measured' | 'estimated';

/** A member and the rate their saved time is valued at. */
export interface TimeSavedRate {
  user_id: number;
  name?: string | null;
  initials?: string | null;
  avatar_url?: string | null;
  rate: number;
  currency: string;
  /** user = their own billing rate; firm = the fallback set here; default = PONS's. */
  source: 'user' | 'firm' | 'default';
}

export interface TimeSavedSettings {
  rates?: TimeSavedRate[];
  kinds: TimeSavedAssumptionRow[];
  draft_types: TimeSavedAssumptionRow[];
  firm_hourly_rate: number | null;
  default_hourly_rate: number;
  currency: string;
  pons_share: Record<TimeSavedKind, number>;
  can_edit: boolean;
}

export interface TimeSavedItem {
  manual_hours: number;
  pons_hours: number;
  saved_hours: number;
  value_saved: number;
  hourly_rate: number;
  currency: string;
  source: TimeSavedSource;
  pons_source?: PonsSource;
  type_name?: string | null;
  can_edit: boolean;
}

const unwrap = <T>(data: any): T => (data && data.data !== undefined ? data.data : data) as T;

export async function getTimeSavedSettings(): Promise<TimeSavedSettings> {
  const res = await apis.get('/time-saved/settings');
  return unwrap<TimeSavedSettings>(res.data);
}

export async function saveTimeSavedSettings(payload: {
  kinds: Partial<TimeSavedAssumptionRow>[];
  draft_types: Partial<TimeSavedAssumptionRow>[];
  firm_hourly_rate: number | null;
}): Promise<TimeSavedSettings> {
  const res = await apis.put('/time-saved/settings', payload);
  return unwrap<TimeSavedSettings>(res.data);
}

export async function getTimeSavedItem(kind: TimeSavedItemKind, uuid: string): Promise<TimeSavedItem> {
  const res = await apis.get(`/time-saved/items/${kind}/${uuid}`);
  return unwrap<TimeSavedItem>(res.data);
}

/** Set how long this item takes by hand; `null` goes back to the firm's figure.
 *  Hours with PONS are measured, so they are never sent. */
export async function setTimeSavedItem(
  kind: TimeSavedItemKind, uuid: string, manual_hours: number | null,
): Promise<TimeSavedItem> {
  const res = await apis.put(`/time-saved/items/${kind}/${uuid}`, { manual_hours });
  return unwrap<TimeSavedItem>(res.data);
}

/** A processing time: "40 s", "6 min", "1.5 h". */
export const formatDuration = (h: number | null | undefined, t: (k: string, o?: any) => string): string => {
  const hours = h ?? 0;
  if (hours <= 0) return t('{{n}} min', { n: 0 });
  if (hours < 1 / 60) return t('{{n}} s', { n: Math.max(1, Math.round(hours * 3600)) });
  if (hours < 1) return t('{{n}} min', { n: Math.round(hours * 60) });
  return `${Number(hours.toFixed(2))} h`;
};

/** "15 h", "1.5 h", "0.25 h" - hours without trailing zeros. */
export const formatHours = (h: number | null | undefined): string =>
  `${Number((h ?? 0).toFixed(2))} h`;
